import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { employeesAPI, locationsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Search, UserPlus, Stethoscope, ClipboardList, HeartPulse, Users as UsersIcon } from 'lucide-react';
import DataPagination from '../components/DataPagination';

const roleMeta = {
  doctor: { label: 'Doctor', icon: Stethoscope, badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  nurse: { label: 'Nurse', icon: HeartPulse, badge: 'bg-pink-50 text-pink-700 border-pink-200' },
  front_desk: { label: 'Front Desk', icon: ClipboardList, badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const EMPLOYEE_ROLES = ['doctor', 'nurse', 'front_desk'];

const Employees = () => {
  const { user: currentUser, isClinicAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState({ full_name: '', email: '', role: 'front_desk', location_ids: [] });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, searchQuery]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [empRes, locationsRes] = await Promise.all([
        employeesAPI.getAll({ page: currentPage, page_size: pageSize, search: searchQuery || undefined }),
        locationsAPI.getAll()
      ]);
      const paginated = empRes.data;
      setEmployees(paginated.items || []);
      setTotalItems(paginated.total || 0);
      setTotalPages(paginated.total_pages || 0);
      const locData = locationsRes.data.items || locationsRes.data;
      setLocations(Array.isArray(locData) ? locData : []);
    } catch (error) {
      toast.error('Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ full_name: '', email: '', role: 'front_desk', location_ids: currentUser?.location_ids || [] });
    setEditingEmployee(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (emp) => {
    setEditingEmployee(emp);
    setFormData({
      full_name: emp.full_name,
      email: emp.email || '',
      role: emp.role,
      location_ids: emp.location_ids || []
    });
    setDialogOpen(true);
  };

  const toggleLocation = (locationId) => {
    setFormData(prev => {
      const ids = prev.location_ids || [];
      return ids.includes(locationId)
        ? { ...prev, location_ids: ids.filter(id => id !== locationId) }
        : { ...prev, location_ids: [...ids, locationId] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name.trim()) {
      toast.error('Name is required');
      return;
    }
    const emailTrimmed = formData.email?.trim() || '';
    if (!emailTrimmed) {
      toast.error('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
      toast.error('Please enter a valid email address');
      return;
    }
    const payload = {
      full_name: formData.full_name,
      email: emailTrimmed,
      role: formData.role,
      location_ids: formData.location_ids.length > 0 ? formData.location_ids : null
    };
    try {
      if (editingEmployee) {
        await employeesAPI.update(editingEmployee.id, { ...payload, is_active: editingEmployee.is_active });
        toast.success('Employee updated');
      } else {
        await employeesAPI.create(payload);
        toast.success('Employee added');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save employee');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this employee? This does NOT delete any login user.')) return;
    try {
      await employeesAPI.delete(id);
      toast.success('Employee removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove employee');
    }
  };

  // Only clinic admins can access the Employees page
  if (!isClinicAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
            <UserPlus className="w-6 h-6 text-teal-600" /> Employees
          </h1>
          <p className="text-sm text-stone-500 mt-1">Add and manage your clinic staff</p>
        </div>
        <Button onClick={openAddDialog} className="bg-teal-600 hover:bg-teal-700" data-testid="add-employee-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Employee
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <Input
          placeholder="Search employees..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
          className="pl-9"
          data-testid="employee-search-input"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-stone-400">Loading...</div>
          ) : employees.length === 0 ? (
            <div className="p-10 text-center text-stone-400 flex flex-col items-center gap-2">
              <UsersIcon className="w-10 h-10 text-stone-300" />
              <p>No employees yet. Click "Add Employee" to create one.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.map((emp) => {
                  const meta = roleMeta[emp.role] || { label: emp.role, icon: UsersIcon, badge: 'bg-stone-50 text-stone-700 border-stone-200' };
                  const RoleIcon = meta.icon;
                  return (
                    <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`}>
                      <TableCell className="font-medium text-stone-900">{emp.full_name}</TableCell>
                      <TableCell className="text-stone-600">{emp.email || <span className="text-stone-400">—</span>}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${meta.badge}`}>
                          <RoleIcon className="w-3.5 h-3.5" /> {meta.label}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${emp.is_active ? 'bg-green-50 text-green-700' : 'bg-stone-100 text-stone-500'}`}>
                          {emp.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(emp)} title="Edit employee" data-testid={`edit-employee-${emp.id}`}>
                            <Edit className="w-4 h-4 text-stone-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(emp.id)} title="Remove employee" data-testid={`delete-employee-${emp.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <DataPagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
        />
      )}

      {/* Add Employee Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
              <DialogDescription>{editingEmployee ? 'Update this staff member\'s details.' : 'Create a new staff member.'}</DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-4">
              <div>
                <Label htmlFor="emp-name">Full Name *</Label>
                <Input id="emp-name" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} placeholder="John Doe" data-testid="employee-name-input" />
              </div>
              <div>
                <Label htmlFor="emp-email">Email *</Label>
                <Input id="emp-email" type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="john@clinic.com" data-testid="employee-email-input" />
              </div>
              <div>
                <Label>Role *</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger data-testid="employee-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EMPLOYEE_ROLES.map(r => <SelectItem key={r} value={r}>{roleMeta[r].label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {locations.length > 0 && (
                <div>
                  <Label>Locations (optional)</Label>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border border-stone-200 rounded-lg p-3">
                    {locations.map(loc => (
                      <label key={loc.id} className="flex items-center gap-2 cursor-pointer text-sm">
                        <input type="checkbox" checked={formData.location_ids.includes(loc.id)} onChange={() => toggleLocation(loc.id)} className="w-4 h-4 rounded text-teal-600" />
                        <span className="text-stone-700">{loc.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="submit-employee-btn">{editingEmployee ? 'Save Changes' : 'Add Employee'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Employees;
